// Tiny SEO hook — sets document.title, meta description, canonical,
// Open Graph + Twitter card tags for a single page. All mutations are
// reverted on unmount so route changes never leak stale tags.
//
// Usage inside a page component:
//   useSeo({
//     title: 'About 5DBS — Who we are',
//     description: '5DBS is …',
//     path: '/about',
//     image: 'https://fivedbs.com/og-about.jpg', // optional
//   });
import { useEffect } from 'react';

const SITE = 'https://fivedbs.com';
const DEFAULT_IMAGE = `${SITE}/og-default.jpg`;

const upsertMeta = (attr, key, content) => {
  if (typeof document === 'undefined') return () => {};
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  const created = !el;
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  const prev = el.getAttribute('content');
  el.setAttribute('content', content || '');
  return () => { if (created) el.remove(); else el.setAttribute('content', prev || ''); };
};

const upsertLink = (rel, href) => {
  if (typeof document === 'undefined') return () => {};
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  const created = !el;
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return () => { if (created) el.remove(); else el.setAttribute('href', prev || ''); };
};

export function useSeo({ title, description, path = '/', image, type = 'website' }) {
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const url = `${SITE}${path}`;
    const img = image || DEFAULT_IMAGE;
    const prevTitle = document.title;
    if (title) document.title = title.includes('5DBS') ? title : `${title} · 5DBS`;

    const cleanups = [
      description ? upsertMeta('name', 'description', description) : null,
      upsertLink('canonical', url),
      upsertMeta('property', 'og:type', type),
      upsertMeta('property', 'og:url', url),
      title ? upsertMeta('property', 'og:title', title) : null,
      description ? upsertMeta('property', 'og:description', description) : null,
      upsertMeta('property', 'og:image', img),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      title ? upsertMeta('name', 'twitter:title', title) : null,
      description ? upsertMeta('name', 'twitter:description', description) : null,
      upsertMeta('name', 'twitter:image', img),
    ].filter(Boolean);

    return () => { document.title = prevTitle; cleanups.forEach((fn) => fn && fn()); };
  }, [title, description, path, image, type]);
}

export default useSeo;
